import { useState, useEffect } from 'react';
import { CONVERSATION_KANBAN_SORT_MODES, isCardDensity, type CardDensity, type KanbanSortMode } from '@troia-v3/types';

/**
 * Chaves do localStorage para preferências do Attendance Management
 */
const STORAGE_KEYS = {
  SIDEBAR_OPEN: 'attendance_sidebar_open',
  KANBAN_SORT_MODE: 'attendance_kanban_sort_mode',
  CARD_DENSITY: 'attendance_card_density',
} as const;

/** Fallback quando o valor persistido é ausente/inválido (fora da whitelist atual). */
const DEFAULT_SORT_MODE: KanbanSortMode = 'created';

/** Padrão do Atendimento: card Compacto (`06 Barra de esfriamento`) — spec § 1. */
const DEFAULT_CARD_DENSITY: CardDensity = 'compact';

/**
 * Interface de preferências do Attendance Management
 */
export interface AttendancePreferences {
  isSidebarOpen: boolean;
  /** Modo de ordenação do kanban de atendimentos (SP4). */
  sortMode: KanbanSortMode;
  /** Tamanho do card do kanban de atendimentos. */
  cardDensity: CardDensity;
}

/**
 * Hook customizado para gerenciar preferências do Attendance Management com persistência em localStorage
 *
 * Salva e recupera automaticamente:
 * - Estado da sidebar (aberta/fechada)
 *
 * @example
 * ```tsx
 * const {
 *   isSidebarOpen,
 *   setIsSidebarOpen
 * } = useAttendancePreferences();
 * ```
 */
export const useAttendancePreferences = () => {
  // Carregar preferências do localStorage na inicialização
  const [isSidebarOpen, setIsSidebarOpenState] = useState<boolean>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.SIDEBAR_OPEN);
      // Default: sidebar fechada (false)
      return stored ? JSON.parse(stored) : false;
    } catch {
      return false;
    }
  });

  // Salvar isSidebarOpen no localStorage sempre que mudar
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEYS.SIDEBAR_OPEN, JSON.stringify(isSidebarOpen));
    } catch (error) {
      console.error('Erro ao salvar isSidebarOpen no localStorage:', error);
    }
  }, [isSidebarOpen]);

  // Carregar sortMode do localStorage na inicialização — whitelist contra
  // CONVERSATION_KANBAN_SORT_MODES na LEITURA: protege contra valor legado/
  // inválido (ex.: sobra de outro módulo do kanban, como 'manual', que não
  // existe pra atendimentos) caindo silenciosamente no fallback 'created'.
  const [sortMode, setSortModeState] = useState<KanbanSortMode>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.KANBAN_SORT_MODE);
      return stored && (CONVERSATION_KANBAN_SORT_MODES as readonly string[]).includes(stored)
        ? (stored as KanbanSortMode)
        : DEFAULT_SORT_MODE;
    } catch {
      return DEFAULT_SORT_MODE;
    }
  });

  // Salvar sortMode no localStorage sempre que mudar
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEYS.KANBAN_SORT_MODE, sortMode);
    } catch (error) {
      console.error('Erro ao salvar sortMode no localStorage:', error);
    }
  }, [sortMode]);

  // Mesma disciplina do sortMode acima: whitelist na LEITURA, fallback no padrão
  // do módulo. Aqui o risco concreto é a coluna virtualizada receber uma
  // densidade inexistente e não achar tokens de estimativa de altura.
  const [cardDensity, setCardDensityState] = useState<CardDensity>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.CARD_DENSITY);
      return isCardDensity(stored) ? stored : DEFAULT_CARD_DENSITY;
    } catch {
      return DEFAULT_CARD_DENSITY;
    }
  });

  // Salvar cardDensity no localStorage sempre que mudar
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEYS.CARD_DENSITY, cardDensity);
    } catch (error) {
      console.error('Erro ao salvar cardDensity no localStorage:', error);
    }
  }, [cardDensity]);

  return {
    // Estado da sidebar
    isSidebarOpen,
    setIsSidebarOpen: setIsSidebarOpenState,
    // Modo de ordenação do kanban de atendimentos
    sortMode,
    setSortMode: setSortModeState,
    // Tamanho do card do kanban de atendimentos
    cardDensity,
    setCardDensity: setCardDensityState,
  };
};
